<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { match } from "@/helpers/string";
import { useConfigurationStore } from "@/store/configurationStore";

import type { supporttedEventNames } from "@/dsComponents/Dropdown/types";
import type { JobOption } from "@/types/job";

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import { DropdownInput, DropdownItem } from "@/dsComponents/Dropdown";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";

interface JobsDropdownProps {
    modelValue: JobOption | null;
    placeholder?: string;
    icon?: string;
    error?: boolean;
    width?: string;
    height?: string;
    readonly?: boolean;
    fitContent?: boolean;
    showIcon?: boolean;
    size?: "xs" | "sm" | "md";
    autoOpen?: boolean;
    triggerIconOnly?: string;
    fullWidth?: boolean;
    excludeJobIds?: string[];
    disabled?: boolean;
}

const props = withDefaults(defineProps<JobsDropdownProps>(), {
    icon: "work",
    width: "320px",
    height: "220px",
    size: "sm",
    fitContent: true,
    showIcon: true,
    autoOpen: false,
    fullWidth: false,
    disabled: false,
});

const emit = defineEmits<{
    (event: "open"): void;
    (event: "close"): void;
    (event: "update:modelValue", value: JobOption | null): void;
}>();

const { t } = useI18n();
const configStore = useConfigurationStore();

const jobs = ref<JobOption[]>([]);
const dropdownJobs = ref<JobOption[]>([]);
const jobSearch = ref("");
const open = ref(false);

const toggle = () => {
    open.value = !open.value;
};

const close = () => {
    jobSearch.value = "";
    dropdownJobs.value = jobs.value;
    open.value = false;
};

const handleControlledDropdownChange = (event: supporttedEventNames) => {
    if (event === "clickaway") close();
};

const handleJobSelect = (job: string) => {
    emit("update:modelValue", jobs.value.find((j: JobOption) => j.value === job) ?? null);
    close();
};

const handleJobSearchChange = (value: string) => {
    if (!value.trim()) {
        dropdownJobs.value = jobs.value;
    } else {
        const filteredJobs = jobs.value.filter((job) => match(job.label, value));
        dropdownJobs.value = filteredJobs;
    }
};

const applyJobsFilter = () => {
    const storedJobs = configStore.jobOptions.sort((a: JobOption, b: JobOption) => (a.label > b.label ? 1 : -1));
    const excludeSet = new Set(props.excludeJobIds ?? []);
    const filtered = excludeSet.size === 0 ? storedJobs : storedJobs.filter((job) => !excludeSet.has(job.value));
    jobs.value = filtered;
    dropdownJobs.value = jobSearch.value.trim() ? filtered.filter((job) => match(job.label, jobSearch.value)) : filtered;
};

onMounted(() => {
    if (props.autoOpen) {
        open.value = true;
    }
});

watch(
    () => props.autoOpen,
    (newValue) => {
        if (newValue) {
            open.value = true;
        }
    }
);

watch([() => props.excludeJobIds, () => configStore.jobOptions], () => applyJobsFilter(), { deep: true, immediate: true });

const openDropdown = () => {
    open.value = true;
};

defineExpose({
    openDropdown,
});
</script>
<template>
    <div>
        <ControlledDropdown
            :width="width"
            :height="height"
            :open="open"
            :fit-content="fitContent && !fullWidth"
            :full-width="fullWidth"
            @change="handleControlledDropdownChange"
        >
            <template #trigger-zone>
                <ComboButton
                    :disabled="disabled"
                    :focus="open"
                    :icon-only="triggerIconOnly"
                    :icon-left="!triggerIconOnly && showIcon ? icon : undefined"
                    :placeholder="!triggerIconOnly && !modelValue"
                    :error="error"
                    :readonly="readonly"
                    :size="size"
                    :fullWidth="!triggerIconOnly && fullWidth"
                    @click="toggle"
                >
                    {{ modelValue?.label ?? placeholder ?? t("page.order.jobPlaceholder") }}
                </ComboButton>
            </template>
            <template #dropdown-content>
                <DropdownInput :focused="open" v-model="jobSearch" @update:modelValue="handleJobSearchChange" />
                <div class="job-container">
                    <RecycleScroller class="dropdown-content-recycler" :items="dropdownJobs" :item-size="42" key-field="value" v-slot="{ item }">
                        <div>
                            <DropdownItem @click="() => handleJobSelect(item.value)">{{ item.label }}</DropdownItem>
                        </div>
                    </RecycleScroller>
                </div>
            </template>
        </ControlledDropdown>
    </div>
</template>
<style scoped lang="scss">
.job-container {
    padding-top: 4px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow: hidden;
    height: 100%;

    .dropdown-content-recycler {
        height: 100%;
    }
}
</style>
